import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import { listData } from "../services/api";
import { deleteEvent, listEvents, publicEventUrl } from "./services/api";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "draft", label: "Rascunho" },
  { value: "published", label: "Publicado" },
  { value: "sold_out", label: "Esgotado" },
  { value: "archived", label: "Arquivado" },
];

function formatBRL(cents, currency = "BRL") {
  if (cents == null) return "—";
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency,
    }).format(cents / 100);
  } catch (_e) {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
      timeZone: "America/Sao_Paulo",
    }).format(new Date(iso));
  } catch (_e) {
    return iso;
  }
}

function statusBadge(status) {
  const map = {
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    published: "bg-emerald-100 text-emerald-800 border-emerald-200",
    sold_out: "bg-amber-100 text-amber-800 border-amber-200",
    archived: "bg-slate-100 text-slate-400 border-slate-200",
  };
  const label = {
    draft: "Rascunho",
    published: "Publicado",
    sold_out: "Esgotado",
    archived: "Arquivado",
  };
  const cls = map[status] || map.draft;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold uppercase tracking-wide ${cls}`}
    >
      {label[status] || status || "—"}
    </span>
  );
}

export default function Events() {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [channels, setChannels] = useState([]);

  const [filterSearch, setFilterSearch] = useState("");
  const [filterChannel, setFilterChannel] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  async function loadChannels() {
    const resp = await listData({
      table: "channels",
      term: "",
      value: "",
      limit: 0,
      cursor: "",
    });
    if (resp instanceof Error) {
      toast.error(resp.message);
      return;
    }
    setChannels(resp.data || []);
  }

  async function loadEvents() {
    setIsLoading(true);
    const resp = await listEvents({
      channelId: filterChannel || undefined,
      status: filterStatus || undefined,
      search: filterSearch || undefined,
      limit: 100,
    });
    if (resp instanceof Error) {
      toast.error(resp.message);
      setIsLoading(false);
      return;
    }
    // The backend appends a trailing { count, cursor } sentinel when a limit
    // is set (cursor-based pagination contract). Strip it here.
    const data = (resp.data || []).filter((item) => !item.count);
    setEvents(data);
    setIsLoading(false);
  }

  useEffect(() => {
    loadChannels();
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleFilter() {
    loadEvents();
  }

  function handleClear() {
    setFilterSearch("");
    setFilterChannel("");
    setFilterStatus("");
    // fire once filters reset
    setTimeout(() => loadEvents(), 0);
  }

  async function handleDelete(evt) {
    const ok = window.confirm(
      `Arquivar evento "${evt.title}"?\n\n` +
        `Isso marca o evento como "archived" — inscrições existentes permanecem intactas.`
    );
    if (!ok) return;
    setIsDeleting(true);
    const resp = await deleteEvent(evt.id);
    setIsDeleting(false);
    if (resp instanceof Error) {
      toast.error(resp.message);
      return;
    }
    toast.success("Evento arquivado");
    loadEvents();
  }

  return (
    <div className="w-full max-w-[1400px] mx-auto px-6 pb-8">
      <div className="w-full flex items-center justify-between py-6 border-b border-slate-200 mb-6">
        <div className="flex flex-col">
          <span className="text-slate-500 text-xs uppercase tracking-wider">
            Ferramentas
          </span>
          <h1 className="text-slate-900 text-2xl font-bold tracking-tight m-0">
            Eventos
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="primary" onClick={() => navigate("/tools/events/new")}>
            + Novo evento
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </div>
      </div>

      <Card className="p-4 border-0 shadow-sm mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 min-w-[280px] flex-1">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Buscar
            </label>
            <Form.Control
              type="text"
              placeholder="Título ou slug"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleFilter();
              }}
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Channel
            </label>
            <Form.Select
              value={filterChannel}
              onChange={(e) => setFilterChannel(e.target.value)}
            >
              <option value="">Todos</option>
              {channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Status
            </label>
            <Form.Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="primary" onClick={handleFilter}>
              Filtrar
            </Button>
            {(filterSearch || filterChannel || filterStatus) && (
              <Button variant="outline-secondary" onClick={handleClear}>
                Limpar
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <span className="text-sm font-semibold text-slate-700">
            {events.length} {events.length === 1 ? "evento" : "eventos"}
          </span>
        </div>
        {isLoading ? (
          <div className="w-full text-center py-10">
            <Spinner />
          </div>
        ) : events.length === 0 ? (
          <div className="w-full text-center py-10 text-slate-500">
            Nenhum evento encontrado.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">
                    #
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Título / slug
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Modo
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Preço
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Capacidade
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {events.map((evt, idx) => (
                  <tr
                    key={evt.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-slate-400 tabular-nums">
                      {idx + 1}
                    </td>
                    <td className="px-4 py-3 text-sm max-w-[320px]">
                      <div className="font-semibold text-slate-900 truncate">
                        {evt.title}
                      </div>
                      <a
                        href={publicEventUrl(evt.slug)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Abrir página do evento em nova aba"
                        className="inline-flex items-center gap-1 text-xs text-slate-500 font-mono truncate hover:text-blue-600 no-underline"
                      >
                        /{evt.slug}
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </a>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {formatDate(evt.starts_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {evt.event_mode === "paid" ? (
                        <span className="text-slate-700">Pago</span>
                      ) : (
                        <span className="text-slate-700">
                          Grátis c/ doação
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">
                      {evt.event_mode === "paid"
                        ? formatBRL(evt.unit_price_cents, evt.currency)
                        : "—"}
                      {evt.group_price_cents != null && (
                        <div className="text-xs text-slate-500">
                          grupo {formatBRL(evt.group_price_cents, evt.currency)}{" "}
                          ≥ {evt.group_min_size}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 tabular-nums">
                      {evt.capacity ?? "∞"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {statusBadge(evt.status)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-1 justify-end flex-wrap">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => navigate(`/tools/events/${evt.id}/edit`)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() =>
                            navigate(`/tools/events/${evt.id}/content`)
                          }
                        >
                          Conteúdo
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() =>
                            navigate(`/tools/events/${evt.id}/attendees`)
                          }
                        >
                          Participantes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() =>
                            navigate(`/tools/events/${evt.id}/registrations`)
                          }
                        >
                          Inscrições
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(evt)}
                          disabled={isDeleting}
                        >
                          Arquivar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
